import { Card, CardBody } from '@heroui/react';
import NextLink from 'next/link';

import { BILLING_PERIOD, EXPLORATION_MODE, KREATLI_PLANS, STORAGE_ADD_ON, TRIAL_DAYS } from '../../data/kreatli-plans';

interface KreatliPricingStripProps {
  id?: string;
  title: string;
  /** Extra links shown under the plans, e.g. competitor pricing pages. */
  links?: { href: string; label: string }[];
}

/** Compact plan strip read from `kreatli-plans.ts`, for commercial platform pages. */
export function KreatliPricingStrip({ id = 'pricing-strip-heading', title, links = [] }: KreatliPricingStripProps) {
  return (
    <section className="relative overflow-hidden px-6 py-16 backdrop-blur-lg" aria-labelledby={id}>
      <div className="relative z-10 mx-auto max-w-6xl">
        <div className="mb-8 text-center">
          <h2 id={id} className="mb-4 font-sans text-2xl font-bold sm:text-3xl">
            {title}
          </h2>
          <p className="mx-auto max-w-2xl text-lg text-foreground-500">
            Guest review links on every plan. Billed {BILLING_PERIOD}, with a {TRIAL_DAYS}-day trial on paid plans.
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card className="border border-foreground-200 bg-content1/60 backdrop-blur-lg">
            <CardBody className="flex flex-col gap-2 p-6">
              <h3 className="font-sans text-lg font-semibold">{EXPLORATION_MODE.name}</h3>
              <p className="font-sans text-2xl font-bold">Free</p>
              <p className="text-sm text-foreground-500">{EXPLORATION_MODE.label}</p>
            </CardBody>
          </Card>
          {KREATLI_PLANS.map((plan) => (
            <Card key={plan.id} className="border border-foreground-200 bg-content1/60 backdrop-blur-lg">
              <CardBody className="flex flex-col gap-2 p-6">
                <h3 className="font-sans text-lg font-semibold">{plan.name}</h3>
                <p className="font-sans text-2xl font-bold">
                  {plan.priceMonthly === null ? (
                    'Custom'
                  ) : (
                    <>
                      ${plan.priceMonthly}
                      <span className="text-sm font-normal text-foreground-500">/user/month</span>
                    </>
                  )}
                </p>
                <ul className="text-sm text-foreground-500">
                  {plan.limits.map((limit) => (
                    <li key={limit.label}>{limit.label}</li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          ))}
        </div>
        <p className="mt-6 text-center text-base text-foreground-500">
          Extra storage: {STORAGE_ADD_ON.label}.{' '}
          <NextLink href="/pricing" className="font-medium text-primary underline-offset-2 hover:underline">
            See full pricing
          </NextLink>
          {links.map((link) => (
            <span key={link.href}>
              {' · '}
              <NextLink href={link.href} className="font-medium text-primary underline-offset-2 hover:underline">
                {link.label}
              </NextLink>
            </span>
          ))}
        </p>
      </div>
    </section>
  );
}
